import { MDXEditor, listsPlugin, quotePlugin, headingsPlugin, linkPlugin, linkDialogPlugin, imagePlugin, toolbarPlugin, UndoRedo, Separator, BoldItalicUnderlineToggles, ListsToggle, BlockTypeSelect, CreateLink, InsertImage, MDXEditorMethods } from "@mdxeditor/editor";
import { SaveAsRounded, PublishRounded, Delete, Unpublished, DeleteRounded } from "@mui/icons-material";
import { Stack, Box, ButtonGroup, Button, TextField, Switch, FormControlLabel, Typography } from "@mui/material";
import { Article } from "../../types/Article";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import { deleteDraftArticle, draftArticle, publishArticle, unpublishArticle } from "../../utils/saveArticle";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import { ArticleEditorImageSelector } from "./ArticleEditorImageSelector";
import { getErrorString } from "../../utils/getErrorString";

export type ArticleEditorProps = {
    article?: Article,
    publishedArticle?: Article,
    onUnpublished?: (article: Article) => void,
    onDraftDeleted?: () => void,
    onDraftSaved?: (article: Article) => void,
};
export const ArticleEditor = (props: ArticleEditorProps) => {
    const { article, publishedArticle, onUnpublished, onDraftDeleted, onDraftSaved } = props;
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [id] = useState(article?.id ?? uuidv4());
    const [title, setTitle] = useState(article?.title ?? "");
    const [content, setContent] = useState(article?.content ?? "");
    const [url] = useState(article?.url ?? `https://nadafa-ems.web.app/articles/${id}`);
    const [imageUrl, setImageUrl] = useState(article?.imageUrl ?? "");
    const [deepLink, setDeepLink] = useState(article?.deepLink ?? `https://nadafa-ems.web.app/articles/${id}`);
    const [isFeatured, setIsFeatured] = useState(article?.featured.isFeatured ?? false);
    const [isFeaturedOrder, setIsFeaturedOrder] = useState(article?.featured.isFeaturedOrder ?? 99);

    const [titleHelperText, setTitleHelperText] = useState("");

    const ref = useRef<MDXEditorMethods>(null);

    const validateTitle = () => {
        if (title.trim().length === 0) {
            setTitleHelperText("Please enter title.");
            return false;
        }
        setTitleHelperText("");
        return true;
    };

    const createArticle = (): Article => {
        return {
            id: id,
            title: title,
            content: content,
            url: url,
            imageUrl: imageUrl,
            deepLink: deepLink,
            featured: {
                isFeatured: isFeatured,
                isFeaturedOrder: isFeaturedOrder,
            },
            published: {
                isPublished: false,
                publishedTime: DateTime.now(),
            },
            lastUpdate: DateTime.now()
        }
    };

    const handleDeleteDraftOnClick = () => {
        if (article) {
            deleteDraftArticle(article)
                .then(() => {
                    onDraftDeleted && onDraftDeleted();
                    enqueueSnackbar('Draft has been deleted!', {variant: 'success'});
                    if (!publishedArticle?.published.isPublished) {
                        navigate(`/admin/articles`);
                    }
                })
                .catch((error) => {
                    enqueueSnackbar(getErrorString(error, "Deleting failed!"), {variant: 'error'});
                });
        }
    };

    const handleUnpublishOnClick = () => {
        if (publishedArticle) {
            unpublishArticle(publishedArticle)
                .then((article) => {
                    onUnpublished && onUnpublished(article);
                    enqueueSnackbar('Article unpublished!', {variant: 'success'})
                })
                .catch((error) => {
                    enqueueSnackbar(getErrorString(error, "Unpublishing failed!"), {variant: 'error'});
                });
        }
    };

    const handleDeleteArticleOnClick = () => {
    };

    const handleSaveDraftOnClick = () => {
        if (!validateTitle())
            return;

        let draft: Article = createArticle();
        draftArticle(draft, publishedArticle)
            .then((finalDraft) => {
                // Show saved message
                onDraftSaved && onDraftSaved(finalDraft);
                if (article === undefined) {
                    // We are on creating new article. Navigate to edit article
                    navigate(`/admin/articles/${finalDraft.id}`);
                }
                enqueueSnackbar('Article drafted!', { variant: 'success' });
            })
            .catch((reason) => {
                // Show error
                if (typeof(reason) === 'string') {
                    enqueueSnackbar(reason, { variant: 'error' });
                } else if (typeof(reason) === 'object') {
                    enqueueSnackbar(reason.message, { variant: 'error' });
                } else {
                    enqueueSnackbar('An error has occured while saving the article!', { variant: 'error' });
                }
            });
    };

    const handlePublishArticle = () => {
        if (!validateTitle())
            return;

        let draft: Article = createArticle();
        publishArticle(draft)
            .then((article) => {
                navigate(`/admin/articles`);
                enqueueSnackbar('Article published!', { variant: 'success' });
            })
            .catch((reason) => {
                // Show error
                if (typeof(reason) === 'string') {
                    enqueueSnackbar(reason, { variant: 'error' });
                } else if (typeof(reason) === 'object') {
                    enqueueSnackbar(reason.message, { variant: 'error' });
                } else {
                    enqueueSnackbar('An error has occured while publishing the article!', { variant: 'error' });
                }
            });
    };

    return (
        <Stack spacing={1}>
            {article !== publishedArticle && publishedArticle !== undefined && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption">
                        Drafted Date: {article?.lastUpdate.toFormat("MMMM dd, yyyy hh:mm:ss a")}
                    </Typography>
                    <Button 
                        size="small" 
                        color="error"
                        startIcon={<DeleteRounded />}
                        onClick={handleDeleteDraftOnClick}
                    >
                        <Typography variant="caption">Delete Draft</Typography>
                    </Button>
                </Stack>
            )}
            {publishedArticle !== undefined && publishedArticle.published.isPublished && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption">
                        Published Date: {publishedArticle.published.publishedTime.toFormat("MMMM dd, yyyy hh:mm:ss a")}
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<Unpublished />}
                        onClick={handleUnpublishOnClick}
                    >
                        <Typography variant="caption">Unpublish</Typography>
                    </Button>
                </Stack>
            )}
            <Stack direction="row">
                <FormControlLabel
                    value={isFeatured}
                    control={<Switch checked={isFeatured} onChange={(event) => setIsFeatured(event.currentTarget.checked)} />}
                    label="Feature"
                    labelPlacement="end"
                />
                <Box flexGrow={1} />
                <ButtonGroup variant="contained">
                    {article?.published.isPublished !== publishedArticle?.published.isPublished && (
                        <Button 
                            startIcon={<Delete />} 
                            onClick={handleDeleteArticleOnClick} 
                            disabled={article === undefined}
                            color="error"
                        >
                            Delete
                        </Button>
                    )}
                    <Button startIcon={<SaveAsRounded />} onClick={handleSaveDraftOnClick}>Saved Draft</Button>
                    <Button 
                        startIcon={<PublishRounded />}
                        onClick={handlePublishArticle}
                        color="success"
                        disabled={article === publishedArticle}
                    >
                        Publish
                    </Button>
                </ButtonGroup>
            </Stack>
            <TextField
                variant="standard"
                placeholder="Title"
                value={title}
                onChange={(event) => { setTitle(event.currentTarget.value) }}
                label="Title"
                error={titleHelperText.trim().length > 0 && title.trim().length === 0}
                helperText={titleHelperText}
            />
            {article !== undefined && (
                <ArticleEditorImageSelector article={article} onChange={setImageUrl} />
            )}
            <MDXEditor ref={ref} markdown={article?.content ?? ""} onChange={setContent} plugins={
                [
                    listsPlugin(),
                    quotePlugin(),
                    headingsPlugin(),
                    linkPlugin(),
                    linkDialogPlugin(),
                    imagePlugin(),
                    toolbarPlugin({
                        toolbarClassName: 'my-classname',
                        toolbarContents: () => (
                            <>
                                <UndoRedo />
                                <Separator />
                                <BoldItalicUnderlineToggles />
                                <Separator />
                                <ListsToggle />
                                <Separator />
                                <BlockTypeSelect />
                                <Separator />
                                <CreateLink />
                                <InsertImage />
                            </>
                        )
                    })
                ]
            } />
        </Stack>
    );
};